import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import DashboardShell from "@/components/DashboardShell";
import api from "@/lib/api";
import { CLIENT_NAV } from "@/lib/clientNav";
import {
    LayoutDashboard, FilePlus, FileText, Briefcase, Wallet,
    Download, CreditCard, Landmark, CheckCircle2, AlertTriangle,
    Loader2, RefreshCw, Ban, ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";

const NAV = CLIENT_NAV;

const STATUS_META = {
    open:       { label: "En cours",       color: "#B45309", bg: "#FEF3C7" },
    closed:     { label: "Clôturé",         color: "#0B192C", bg: "#E2E8F0" },
    invoiced:   { label: "Facturé",        color: "#0B192C", bg: "#DBEAFE" },
    collecting: { label: "Prélèvement…",   color: "#B45309", bg: "#FEF3C7" },
    collected:  { label: "Encaissé",       color: "#065F46", bg: "#D1FAE5" },
    failed:     { label: "Échec",           color: "#991B1B", bg: "#FEE2E2" },
    cancelled:  { label: "Annulé",         color: "#64748B", bg: "#F1F5F9" },
};

const fmtEUR = (v) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(v || 0);
const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" });
    } catch {
        return iso;
    }
};

export default function ClientBilling() {
    const [current, setCurrent] = useState(null);   // { period, missions, billing_profile }
    const [history, setHistory] = useState([]);
    const [methods, setMethods] = useState([]);
    const [loading, setLoading] = useState(true);

    const loadAll = () => {
        setLoading(true);
        Promise.all([
            api.get("/company/billing/current").then((r) => r.data).catch(() => null),
            api.get("/company/billing/history").then((r) => r.data.periods || []).catch(() => []),
            api.get("/company/payment-methods").then((r) => r.data.methods || []).catch(() => []),
        ]).then(([cur, hist, mth]) => {
            setCurrent(cur);
            setHistory(hist);
            setMethods(mth);
        }).finally(() => setLoading(false));
    };

    useEffect(() => { loadAll(); }, []);

    const downloadPdf = async (periodId, invoiceNumber) => {
        try {
            const resp = await api.get(`/company/billing/periods/${periodId}/invoice.pdf`, {
                responseType: "blob",
            });
            const blob = new Blob([resp.data], { type: "application/pdf" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `${invoiceNumber || periodId}.pdf`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "PDF indisponible");
        }
    };

    const period = current?.period;
    const missionsCount = (current?.missions || []).length;
    const profile = current?.billing_profile || {};
    const outstandingEur = ((profile.outstanding_cents || 0) / 100);
    const creditEur = ((profile.credit_limit_cents || 0) / 100);
    const hasPm = methods.length > 0;
    const defaultPm = methods.find((m) => m.is_default) || methods[0];

    return (
        <DashboardShell title="Espace client" nav={NAV}>
            <div className="flex justify-between items-end flex-wrap gap-4 mb-8">
                <div>
                    <div className="overline mb-2 text-[#F5A623]">Facturation groupée · Cycle 10 jours</div>
                    <h1 className="font-display text-3xl md:text-4xl">
                        Historique & prélèvements
                    </h1>
                    <p className="text-sm text-slate-500 mt-2 max-w-xl">
                        Toutes vos missions signées sont regroupées sur un cycle de 10 jours
                        et prélevées automatiquement par SEPA ou carte à la clôture. Retrouvez
                        ici vos factures, l'encours à prélever et vos moyens de paiement.
                    </p>
                </div>
            </div>

            {loading && (
                <div className="text-center py-12 text-slate-400 text-sm">
                    <Loader2 className="inline animate-spin mr-2" size={16} /> Chargement…
                </div>
            )}

            {!loading && (
                <>
                    {/* KPIs */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                        <KPI label="Cycle en cours" value={period ? `${period.period_start.slice(8, 10)} → ${period.period_end.slice(8, 10)}` : "—"} />
                        <KPI label="Encours à prélever" value={fmtEUR(outstandingEur)} accent />
                        <KPI label="Missions du cycle" value={missionsCount} />
                        <KPI label="Prochain prélèvement" value={fmtDate(period?.scheduled_debit_at)} />
                    </div>

                    {/* Current cycle card */}
                    {period && (
                        <section className="sd-card p-6 mb-8 border-l-4 border-[#F5A623]" data-testid="client-current-period">
                            <div className="flex justify-between items-start flex-wrap gap-4 mb-4">
                                <div>
                                    <div className="overline text-[#F5A623] mb-1">Cycle en cours</div>
                                    <h2 className="font-display text-xl">
                                        {fmtDate(period.period_start)} → {fmtDate(period.period_end)}
                                    </h2>
                                </div>
                                <div className="text-right">
                                    <div className="overline">Total TTC estimé</div>
                                    <div className="font-display text-2xl text-[#0B192C]">
                                        {fmtEUR(period.total_ttc || 0)}
                                    </div>
                                </div>
                            </div>
                            <div className="grid grid-cols-3 gap-4 text-center text-sm mb-4">
                                <MiniStat label="Prestations HT" value={fmtEUR(period.total_ht || 0)} />
                                <MiniStat label="Commission HT" value={fmtEUR(period.platform_fee_ht || 0)} />
                                <MiniStat label="TVA sur commission" value={fmtEUR(period.vat_amount || 0)} />
                            </div>
                            <div className="border-t border-slate-100 pt-4">
                                <div className="text-xs font-semibold text-slate-600 mb-2">
                                    Missions du cycle
                                </div>
                                {(current?.missions || []).length === 0 ? (
                                    <div className="text-xs text-slate-400 py-2">
                                        Aucune mission encore attachée à ce cycle. Signez un devis pour démarrer.
                                    </div>
                                ) : (
                                    <ul className="divide-y divide-slate-100 text-sm">
                                        {(current?.missions || []).map((m) => (
                                            <li key={m.id} className="py-2 flex justify-between items-center">
                                                <div>
                                                    <div className="text-[13px] text-[#0B192C]">{m.title}</div>
                                                    <div className="text-[11px] text-slate-400">
                                                        {fmtDate(m.date)} · {m.people_count} pro{m.people_count > 1 ? "s" : ""}
                                                    </div>
                                                </div>
                                                <BillingStatusBadge status={m.billing_status} />
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        </section>
                    )}

                    {/* Credit + Payment method */}
                    <div className="grid md:grid-cols-2 gap-4 mb-8">
                        <section className="sd-card p-6" data-testid="client-credit-card">
                            <div className="overline mb-2 flex items-center gap-2">
                                <ShieldCheck size={12} className="text-emerald-600" /> Plafond de crédit
                            </div>
                            <div className="flex items-baseline gap-2 mb-2">
                                <div className="font-display text-3xl">{fmtEUR(outstandingEur)}</div>
                                <div className="text-sm text-slate-400">/ {fmtEUR(creditEur)}</div>
                            </div>
                            <div className="w-full h-2 rounded-full bg-slate-100 overflow-hidden mb-3">
                                <div
                                    className="h-2 rounded-full transition-all"
                                    style={{
                                        width: `${Math.min(100, (outstandingEur / Math.max(creditEur, 1)) * 100)}%`,
                                        background: outstandingEur >= creditEur * 0.8 ? "#DC2626" : "#F5A623",
                                    }}
                                />
                            </div>
                            <StatusPill status={profile.credit_status || "pending_verification"} />
                            <p className="text-[11px] text-slate-400 mt-3">
                                Besoin d'un plafond plus élevé ? Contactez <b>contact@renforteo.fr</b>.
                            </p>
                        </section>

                        <section className="sd-card p-6" data-testid="client-methods-card">
                            <div className="overline mb-2 flex items-center gap-2">
                                <CreditCard size={12} className="text-[#F5A623]" /> Moyens de paiement
                            </div>
                            {!hasPm ? (
                                <>
                                    <div className="text-sm text-red-700 font-semibold mb-2 flex items-center gap-2">
                                        <AlertTriangle size={14} /> Aucun moyen enregistré
                                    </div>
                                    <p className="text-xs text-slate-500 mb-3">
                                        Un mandat SEPA ou une carte est nécessaire pour clôturer le
                                        cycle en cours. Il sera enregistré à la prochaine signature de devis.
                                    </p>
                                    <Link to="/devis" className="btn-gold text-xs px-3 py-2" data-testid="client-add-pm-btn">
                                        Signer un devis
                                    </Link>
                                </>
                            ) : (
                                <ul className="divide-y divide-slate-100">
                                    {methods.map((m) => (
                                        <li key={m.id} className="py-2 flex items-center justify-between">
                                            <div className="flex items-center gap-2">
                                                {m.type === "sepa_debit" ? (
                                                    <Landmark size={14} className="text-[#0B192C]" />
                                                ) : (
                                                    <CreditCard size={14} className="text-[#0B192C]" />
                                                )}
                                                <div>
                                                    <div className="text-sm text-[#0B192C]">
                                                        {m.type === "sepa_debit" ? "SEPA" : (m.brand || "CB")} ····{m.last4}
                                                    </div>
                                                    <div className="text-[11px] text-slate-400">
                                                        {m.is_default ? "Par défaut" : "Secondaire"} · {m.mandate_status}
                                                    </div>
                                                </div>
                                            </div>
                                            {m.is_default && <CheckCircle2 size={14} className="text-emerald-600" />}
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </section>
                    </div>

                    {/* History table */}
                    <section className="sd-card overflow-hidden" data-testid="client-billing-history">
                        <div className="p-6 border-b border-slate-100">
                            <div className="overline mb-1">Historique</div>
                            <h2 className="font-display text-xl">Cycles précédents</h2>
                        </div>
                        <table className="w-full text-sm">
                            <thead className="bg-slate-50 border-b border-slate-200">
                                <tr>
                                    <Th>Cycle</Th>
                                    <Th>Facture</Th>
                                    <Th align="right">Total TTC</Th>
                                    <Th>Statut</Th>
                                    <Th>Prélèvement</Th>
                                    <Th align="right">Actions</Th>
                                </tr>
                            </thead>
                            <tbody>
                                {history.length === 0 && (
                                    <tr>
                                        <td colSpan={6} className="text-center py-8 text-slate-400">
                                            Aucun cycle passé. Vos factures apparaîtront ici après clôture.
                                        </td>
                                    </tr>
                                )}
                                {history.map((p) => {
                                    const meta = STATUS_META[p.status] || STATUS_META.open;
                                    return (
                                        <tr key={p.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`client-billing-row-${p.id}`}>
                                            <Td>
                                                <div className="text-[13px] text-[#0B192C]">
                                                    {fmtDate(p.period_start)} → {fmtDate(p.period_end)}
                                                </div>
                                                <div className="text-[11px] text-slate-400">
                                                    {(p.mission_ids || []).length} mission{(p.mission_ids || []).length > 1 ? "s" : ""}
                                                </div>
                                            </Td>
                                            <Td>
                                                {p.invoice_number ? (
                                                    <span className="font-mono text-[11px] text-slate-500">{p.invoice_number}</span>
                                                ) : (
                                                    <span className="text-slate-300 text-[11px]">—</span>
                                                )}
                                            </Td>
                                            <Td align="right">
                                                <span className="font-semibold text-[#0B192C]">{fmtEUR(p.total_ttc)}</span>
                                            </Td>
                                            <Td>
                                                <span
                                                    className="inline-block px-2 py-0.5 rounded-full text-[11px] font-semibold"
                                                    style={{ background: meta.bg, color: meta.color }}
                                                >
                                                    {meta.label}
                                                </span>
                                                {p.status === "failed" && p.stripe_error && (
                                                    <div className="text-[11px] text-red-700 mt-1">{p.stripe_error}</div>
                                                )}
                                            </Td>
                                            <Td>
                                                <div className="text-[13px]">{fmtDate(p.scheduled_debit_at)}</div>
                                                {p.collected_at && (
                                                    <div className="text-[11px] text-emerald-700">
                                                        Encaissé {fmtDate(p.collected_at)}
                                                    </div>
                                                )}
                                            </Td>
                                            <Td align="right">
                                                {p.invoice_number && (
                                                    <button
                                                        onClick={() => downloadPdf(p.id, p.invoice_number)}
                                                        className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-[#0B192C]"
                                                        data-testid={`client-pdf-${p.id}`}
                                                    >
                                                        <Download size={12} /> PDF
                                                    </button>
                                                )}
                                            </Td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </section>

                    <div className="mt-8 flex justify-center">
                        <button onClick={loadAll} className="text-xs text-slate-500 hover:text-slate-800 inline-flex items-center gap-1">
                            <RefreshCw size={12} /> Rafraîchir
                        </button>
                    </div>
                </>
            )}
        </DashboardShell>
    );
}

function KPI({ label, value, accent }) {
    return (
        <div className="sd-card p-4">
            <div className="overline mb-1">{label}</div>
            <div className={`font-display text-2xl ${accent ? "text-[#F5A623]" : "text-[#0B192C]"}`}>
                {value}
            </div>
        </div>
    );
}
function MiniStat({ label, value }) {
    return (
        <div className="bg-slate-50 rounded-xl p-3">
            <div className="overline">{label}</div>
            <div className="font-semibold text-[#0B192C] text-lg">{value}</div>
        </div>
    );
}
const Th = ({ children, align }) => (
    <th className={`px-4 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider ${align === "right" ? "text-right" : ""}`}>
        {children}
    </th>
);
const Td = ({ children, align }) => (
    <td className={`px-4 py-3 ${align === "right" ? "text-right" : ""}`}>{children}</td>
);

function BillingStatusBadge({ status }) {
    const map = {
        awaiting_completion: { label: "En attente feuille de temps", color: "#64748B", bg: "#F1F5F9" },
        ready_for_billing:   { label: "Prête à facturer",           color: "#B45309", bg: "#FEF3C7" },
        billed:              { label: "Facturée",                     color: "#0B192C", bg: "#DBEAFE" },
        collected:           { label: "Encaissée",                    color: "#065F46", bg: "#D1FAE5" },
        paid_to_pro:         { label: "Reversée au pro",               color: "#065F46", bg: "#D1FAE5" },
    };
    const m = map[status] || { label: status || "—", color: "#64748B", bg: "#F1F5F9" };
    return (
        <span
            className="inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold"
            style={{ background: m.bg, color: m.color }}
        >
            {m.label}
        </span>
    );
}

function StatusPill({ status }) {
    const map = {
        pending_verification: { label: "En vérification", color: "#B45309", bg: "#FEF3C7", Icon: Loader2 },
        active:               { label: "Compte actif",     color: "#065F46", bg: "#D1FAE5", Icon: CheckCircle2 },
        frozen:               { label: "Compte gelé",      color: "#991B1B", bg: "#FEE2E2", Icon: Ban },
        banned:               { label: "Compte banni",     color: "#991B1B", bg: "#FEE2E2", Icon: Ban },
    };
    const m = map[status] || map.pending_verification;
    const Icon = m.Icon;
    return (
        <span
            className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-semibold"
            style={{ background: m.bg, color: m.color }}
        >
            <Icon size={12} /> {m.label}
        </span>
    );
}
